<script setup lang="ts">
import type { Ref } from 'vue';
import type { ReadonlyDeep } from 'type-fest';
import type { HeadersData } from '@/composables/useTable';

import { computed } from 'vue';

import TableLoader from '@/components/loaders/TableLoader.vue';
import SortThTableCell from '@/components/tableCells/SortThTableCell.vue';
import TableEmptyText from '@/components/texts/TableEmptyText.vue';
import KParentTooltip from '@/components/tooltips/KParentTooltip.vue';
import JobIdAndIconLinkUnit from '@/components/units/JobIdAndIconLinkUnit.vue';
import useTable from '@/composables/useTable';
import * as commonConstants from '@/constants/common';
import * as routeNames from '@/constants/routeNames';

import tableStyles from '@/styles/table.module.scss';

export interface DataItem {
  subjectId: number;
  subject: string;
  createdBy: string;
  customer: string;
  updatedBy: string;
  updatedAt: string;
  updatedAtTooltip: string;
  isRfc: boolean;
  ticketStatus: string;
  ticketStatusIcon: string;
  jobs: {
    jobId: number;
    jobDate: string;
  }[];
}

export interface Props {
  loading?: boolean;
  data?: DataItem[];
}
const props = withDefaults(defineProps<Props>(), {
  loading: false,
  data: () => [],
});

const useTableValues = useTable(props);
const { actives, ascs, handleSort } = useTableValues;
const sortedData = useTableValues.sortedData as Readonly<
  Ref<ReadonlyDeep<DataItem[]>>
>;

const headersData = computed<HeadersData>(function () {
  // if (props.isStaff) { // use computed to keep the pattern from other tables
  return [
    {
      value: 'subject',
      title: 'Subject',
    },
    {
      value: 'createdBy',
      title: 'Created by',
    },
    {
      value: 'customer',
      title: 'Customer',
    },
    {
      value: 'updatedBy',
      title: 'Updated by',
    },
    {
      value: 'updatedAt',
      title: 'Updated at',
    },
    {
      value: 'isRfc',
      title: 'RFC',
    },
    {
      value: 'ticketStatus',
      title: 'Ticket status',
    },
    {
      value: 'jobs',
      title: 'Job ID (s)',
    },
  ];
});

const foldColspan = computed(() => {
  return headersData.value.length;
});
</script>

<template>
  <table
    :class="[tableStyles.table, $style.ticketsDashboardTable]"
    data-testid="ticketsDashboardTable"
  >
    <thead>
      <tr>
        <template
          v-for="(headerItem, headerIndex) in headersData"
          :key="headerIndex"
        >
          <SortThTableCell
            :colspan="headerItem.colspan"
            :actives="actives"
            :ascs="ascs"
            :handle-sort="handleSort"
            :header-item-value="headerItem.value"
            :is-not-sortable="headerItem.isNotSortable"
            :class="$style[headerItem.value + 'Th']"
          >
            {{ headerItem.title }}
          </SortThTableCell>
        </template>
      </tr>
    </thead>
    <tr v-if="props.loading">
      <td :colspan="foldColspan" class="!h-64 p-0">
        <TableLoader />
      </td>
    </tr>
    <tr v-else-if="!sortedData?.length">
      <td :colspan="foldColspan" class="border-b-0 p-0">
        <TableEmptyText />
      </td>
    </tr>
    <tbody v-else>
      <template v-for="dataItem in sortedData" :key="dataItem.subjectId">
        <tr>
          <td :class="$style.subjectTd">
            <router-link
              :to="{
                name: routeNames.SERVICE_DESK_TICKET,
                params: {
                  ticket_id: dataItem.subjectId,
                },
              }"
            >
              {{ dataItem.subject }}
            </router-link>
          </td>
          <td :class="$style.createdByTd">
            {{ dataItem.createdBy }}
          </td>
          <td :class="$style.customerTd">
            {{ dataItem.customer }}
          </td>
          <td :class="$style.updatedByTd">
            {{ dataItem.updatedBy }}
          </td>
          <td :class="$style.updatedAtTd">
            <span>
              <KParentTooltip>{{ dataItem.updatedAtTooltip }}</KParentTooltip>
              {{ dataItem.updatedAt }}
            </span>
          </td>
          <td :class="$style.rfcTd">
            <v-icon
              v-if="dataItem.isRfc"
              :class="$style.rfcIcon"
              icon="mdi-frequently-asked-questions"
            >
            </v-icon>
            <template v-else>
              {{ commonConstants.EM_DASH }}
            </template>
          </td>
          <td :class="$style.ticketStatusTd">
            <v-icon :icon="dataItem.ticketStatusIcon"></v-icon>
            {{ dataItem.ticketStatus }}
          </td>
          <td :class="$style.jobsTd">
            <span v-for="job in dataItem.jobs" :key="job.jobId">
              <JobIdAndIconLinkUnit
                :job-id="job.jobId"
                :job-date="job.jobDate"
              />
            </span>
          </td>
        </tr>
      </template>
    </tbody>
  </table>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.ticketsDashboardTable {
  width: 100%;

  & > thead > tr {
    & > th {
      white-space: nowrap;
    }
    & > th {
      position: sticky;
      top: variables.$appBarHeight;
      z-index: 1;
    }
  }
}
  .subjectTh {
  }
  .createdByTh {
    min-width: 110px;
    width: 110px;
  }
  .customerTh {
    min-width: 110px;
    width: 110px;
  }
  .updatedByTh {
    min-width: 110px;
    width: 110px;
  }
  .updatedAtTh {
  }
  .rfcTh {
    min-width: 75px;
    width: 75px;
  }
  .ticketStatusTh {
  }
  .jobsTh {
    min-width: 115px;
    width: 115px;
  }
  .subjectTd {
  }
  .createdByTd {
    min-width: 110px;
    width: 110px;
    word-break: break-word;
  }
  .customerTd {
    min-width: 110px;
    width: 110px;
    word-break: break-word;
  }
  .updatedByTd {
    min-width: 110px;
    width: 110px;
    word-break: break-word;
  }
  .updatedAtTd {
  }
  .rfcTd {
    min-width: 75px;
    width: 75px;
  }
    .rfcIcon {
      color: colors.$color-orange-400;
      font-size: 20px;
    }
  .ticketStatusTd {
  }
  .jobsTd {
  }
</style>
